<template>
    <v-dialog v-model="search" width="400" class="search__dialog">
        <v-card light :loading="loading">
			<div class="px-4 py-6">
				<v-text-field label="Search terms" ref="searchfield" v-model="keywords" v-on:keyup="performSearch" filled autofocus hide-details />
				<div class="d-flex mt-6">
					<v-btn large depressed outlined color="#254485" disabled>
						View A-Z Index
					</v-btn>
					<v-spacer />
					<v-btn large depressed color="primary" dark right @click="searchResults">
						Search<v-icon small right>fas fa-search</v-icon>
					</v-btn>
				</div>
				
				<div v-if="hasESearchPermissions" class="mt-6">
					<div>
						<v-checkbox v-model="useESearch" label="Test New Search" />
					</div>
					<template v-if="useESearch">
						<p class="search-filters__label">Filter Results:</p>
						<QSearchFilters v-model="searchCategories" />
					</template>
				</div>


				<div v-if="search_results.agents.data.length > 0 || search_results.pages.data.length > 0 || search_results.docs.data.length > 0 || elastic_search_results.length > 0">
					<div class="q-subtitle mt-8">
						<h5>Quick Results</h5>
					</div>
                    
					<v-list v-if="useESearch">
						<v-list-item
                            v-for="result in elastic_search_results"
                            :key="`agent-${result.ID}`"
                            @click="openPage(result)"
                        >
                            <v-list-item-content>
                                <v-list-item-title>{{ result.Divisions }}</v-list-item-title>
                                <span class="q-subtitle blue--text text--darken-3">{{ result.Title }}</span>
                            </v-list-item-content>
                            <v-list-item-action v-if="result.IsExternalResource">
                                <v-icon small>fas fa-external-link-alt</v-icon>
                            </v-list-item-action>
                        </v-list-item>
                    </v-list>

					<v-list v-else dense two-line>
						<v-list-item @click="loadAgent(result)" v-for="(result, k) in search_results.agents.data" :key="`agent-${k}`">
							<v-list-item-content>
								<v-list-item-subtitle>{{ result.LeadershipLevel }}</v-list-item-subtitle>
								<v-list-item-title class="q_blue_1--text"><strong>{{ result.AgentName }}</strong></v-list-item-title>
							</v-list-item-content>
							<v-list-item-action>
								<v-icon small>fas fa-user-alt</v-icon>
							</v-list-item-action>
						</v-list-item>
						<v-list-item @click="viewDoc(result)" v-for="(result, k) in search_results.docs.data" :key="`docs-${k}`">
							<v-list-item-content>
								<v-list-item-subtitle>{{ result.CategoryName }}</v-list-item-subtitle>
								<v-list-item-title class="q_blue_1--text"><strong>{{ result.DocTitle }}</strong></v-list-item-title>
							</v-list-item-content>
							<v-list-item-action>
							</v-list-item-action>
						</v-list-item>
						<v-list-item @click="loadPage(result)" v-for="(result, k) in search_results.pages.data" :key="`page-${k}`">
							<v-list-item-content>
								<v-list-item-subtitle>{{ result.category_name }}</v-list-item-subtitle>
								<v-list-item-title class="q_blue_1--text"><strong>{{ result.title }}</strong></v-list-item-title>
							</v-list-item-content>
							<v-list-item-action>
								<v-icon small v-if="result.external_url && result.external_url !== 'internal'">fas fa-external-link-alt</v-icon>
							</v-list-item-action>
						</v-list-item>
					</v-list>
				</div>
			</div>
        </v-card>
    </v-dialog>
</template>

<script>
import { debounce } from 'vue-debounce'

import QuilityAPI from '@/store/API/QuilityAPI.js'
import SearchResult from '@/store/Models/SearchResult'
import QSearchFilters from './QSearchFilters.vue'

const getEmptyResults = () => ({
	pages: {
		data: [],
		meta: {}
	},
	agents: {
		data: [],
		meta: {}
	},
	docs: {
		data: [],
		meta: {}
	},
})

export default {
	components: { QSearchFilters },
    data () {
        return {
            keywords: null,
            search_results: getEmptyResults(),
            elastic_search_results: [],
			searchCategories: {
				Agent: true,
				AgentDocument: true,
				Page: true,
			},
			useESearch: false,
        }
    },
    computed: {
        search: {
            get () { return this.$store.state.show_search },
            set (v) { this.$store.commit('updateShowSearch', v) },
        },
        loading () {
            return this.$store.state.entities.search_result.loading
        },
		searchCategoriesFilter () {
			return Object.entries(this.searchCategories)
				.filter(([_, value]) => value)
				.map(([key]) => key)
				.join(',')
		},
		hasESearchPermissions () {
			return this.hasRole(['SuperAdmin', 'Staff'])
		},
    },
    watch: {
        search (newV) {
            if (newV) {
                SearchResult.create({ data: {} })
                this.keywords = ''
            }
        },
		searchCategoriesFilter () {
			if (this.keywords) {
				this.loadData()
			}
		},
		useESearch () {
			this.elastic_search_results = []
			this.search_results = getEmptyResults()
			if (this.keywords) {
				this.loadData()
			}
		},
    },
    methods: {
        openPage (result) {
            if (result.IsExternalResource) {
                window.open(result.URL, '_blank')
            } else {
                const path = result.URL.replace(/^(([^:/?#]+):)?(\/\/([^/?#]*))/g, '')
                this.$router.push(path)
            }
            this.search = false
        },
        loadPage (result) {
            if (result.external_url && result.external_url !== 'internal') {
                window.open(result.external_url, '_blank')
            } else {
                this.$router.push('/page/' + result.slug)
            }
            this.search = false
        },
        loadData: debounce(function() {
			const options = {
				search: this.keywords,
				itemsPerPage: this.useESearch ? 'quickresults' : 5,
				page: 1,
			}
			const filters = this.useESearch ? { searchModels: this.searchCategoriesFilter } : {}
			const API = this.useESearch ? 'getElasticSearchResults' : 'getSearchResults'
            QuilityAPI[API](filters, options)
				.then((json) => {
					if (this.useESearch) {
						this.elastic_search_results = json?.data || []
					} else {
						this.selectSearchResults(json)
					}
				})
				.catch((err) => {
					if (err.message === 'Aborted') { return }
					console.error(err)
					this.showError('Opps, something went wrong.<br>', err.message)
					this.search_results = getEmptyResults()
				})
        }, 800),
		selectSearchResults (json) {
			if (json.error) {
				this.showError('Opps, something went wrong.<br>', json.message)
				this.search_results = getEmptyResults()
			} else {
				this.search_results = json
			}
		},
        performSearch (e) {
            if (e.keyCode === 13) {
                this.searchResults()
            } else if (this.keywords) {
                this.loadData()
            }
        },
        searchResults () {
            this.$store.commit('updateShowSearch', false)
			const query = this.useESearch
				? { keyword: this.keywords, searchModels: this.searchCategoriesFilter }
				: { keyword: this.keywords }
            this.$router
				.push({ name: 'SearchResults', query })
				.catch(err => { this.showError('Whoops! ' + err) })
        },
        loadAgent (result) {
            this.$router.push('/agents/' + result.AgentCode)
            this.search = false
        },
        viewDoc (doc) {
            window.open(doc.ViewLink, '_blank')
        },
    },
}
</script>

<style lang="scss" scoped>
.search-filters__label {
	margin-bottom: 0.25rem;
	font-size: 0.875rem;
	font-weight: 600;
}
</style>